import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { defineMessages, useIntl } from "react-intl";
import { ReactComponent as ChevronDownIcon } from "../../../images/editor-icons/chevron-down.svg";
import { ReactComponent as CheckIcon } from "../../../images/editor-icons/check.svg";

const messages = defineMessages({
  placeholder: {
    id: "editor.multiselect.placeholder",
    defaultMessage: "Select options...",
    description: "Placeholder text for multiselect dropdown"
  },
  selectedCount: {
    id: "editor.multiselect.selected-count",
    defaultMessage: "{count} layers selected",
    description: "Text showing number of selected layers"
  },
  selectAll: {
    id: "editor.multiselect.select-all",
    defaultMessage: "Select all",
    description: "Button text to select all options"
  },
  deselectAll: {
    id: "editor.multiselect.deselect-all", 
    defaultMessage: "Deselect all",
    description: "Button text to deselect all options"
  }
});

interface MultiSelectOption {
  value: number;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selectedValues: number[];
  onChange: (selectedValues: number[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder,
  disabled = false,
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (value: number) => {
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    
    onChange(newSelectedValues);
  };

  const getDisplayText = () => {
    if (selectedValues.length === 0) {
      return placeholder || intl.formatMessage(messages.placeholder);
    }
    if (selectedValues.length === 1) {
      const option = options.find(opt => opt.value === selectedValues[0]);
      return option ? option.label : "";
    }
    return intl.formatMessage(messages.selectedCount, { count: selectedValues.length });
  };

  const handleSelectAll = () => {
    if (selectedValues.length === options.length) {
      // Deselect all
      onChange([]);
    } else {
      // Select all
      onChange(options.map(opt => opt.value));
    }
  };

  const allSelected = selectedValues.length === options.length;
  const someSelected = selectedValues.length > 0 && selectedValues.length < options.length;

  return (
    <Container ref={containerRef}>
      <Trigger onClick={handleToggle} disabled={disabled}>
        <TriggerText disabled={disabled}>{getDisplayText()}</TriggerText>
        <ChevronIcon $isOpen={isOpen}>
          <ChevronDownIcon />
        </ChevronIcon>
      </Trigger>

      {isOpen && (
        <DropdownContainer>
          <DropdownHeader>
            <SelectAllOption onClick={handleSelectAll}>
              <Checkbox $checked={allSelected} $indeterminate={someSelected}>
                {allSelected && <CheckIcon />}
                {someSelected && <IndeterminateIcon>-</IndeterminateIcon>}
              </Checkbox>
              <OptionLabel>
                {allSelected 
                  ? intl.formatMessage(messages.deselectAll)
                  : intl.formatMessage(messages.selectAll)
                }
              </OptionLabel>
            </SelectAllOption>
          </DropdownHeader>
          
          <OptionsList>
            {options.map((option) => (
              <Option
                key={option.value}
                onClick={() => handleOptionClick(option.value)}
                $selected={selectedValues.includes(option.value)}
              >
                <Checkbox $checked={selectedValues.includes(option.value)}>
                  {selectedValues.includes(option.value) && <CheckIcon />}
                </Checkbox>
                <OptionLabel>{option.label}</OptionLabel>
              </Option>
            ))}
          </OptionsList>
        </DropdownContainer>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const Trigger = styled.button<{ disabled: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  background-color: ${props => props.disabled ? 'var(--color-neutral-10)' : 'var(--color-neutral-10)'};
  border: 1px solid var(--color-neutral-10);
  border-radius: var(--border-radius-default);
  font-family: var(--font-family-body);
  font-size: var(--font-size-30);
  color: ${props => props.disabled ? 'var(--color-neutral-50)' : 'inherit'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: box-shadow 150ms var(--bezier-a);

  &:focus {
    background-color: var(--color-white);
    border: 1px solid var(--color-primary-50);
    box-shadow: 0px 0px 0px 3px var(--color-primary-20);
    outline: 0;
  }

  &:hover:not(:disabled) {
    background-color: var(--color-neutral-5);
  }
`;

const TriggerText = styled.span<{ disabled: boolean }>`
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  color: ${props => props.disabled ? 'var(--color-neutral-50)' : 'inherit'};
`;

const ChevronIcon = styled.div<{ $isOpen: boolean }>`
  width: 16px;
  height: 16px;
  margin-left: 8px;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 200ms ease;
  color: var(--color-neutral-50);

  svg {
    width: 16px;
    height: 16px;
  }
`;

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: var(--color-white);
  border: 1px solid var(--color-neutral-20);
  border-radius: var(--border-radius-default);
  box-shadow: var(--box-shadow-30);
  max-height: 300px;
  overflow-y: auto;
  margin-top: 2px;
`;

const DropdownHeader = styled.div`
  border-bottom: 1px solid var(--color-neutral-10);
`;

const SelectAllOption = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    background-color: var(--color-neutral-5);
  }
`;

const OptionsList = styled.div`
  max-height: 200px;
  overflow-y: auto;
`;

const Option = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  background-color: ${props => props.$selected ? 'var(--color-primary-10)' : 'transparent'};

  &:hover {
    background-color: var(--color-neutral-5);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Checkbox = styled.div<{ $checked: boolean; $indeterminate?: boolean }>`
  width: 20px;
  height: 20px;
  margin-right: 12px;
  border: 2px solid var(--color-neutral-30);
  border-radius: var(--border-radius-10);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: ${props => 
    props.$checked 
      ? 'var(--color-primary-50)' 
      : props.$indeterminate 
        ? 'var(--color-primary-50)' 
        : 'transparent'
  };
  border-color: ${props => 
    props.$checked || props.$indeterminate 
      ? 'var(--color-primary-50)' 
      : 'var(--color-neutral-30)'
  };

  svg {
    width: 12px;
    height: 12px;
    color: var(--color-white);
  }
`;

const IndeterminateIcon = styled.span`
  color: var(--color-white);
  font-size: 16px;
  line-height: 1;
  font-weight: bold;
`;

const OptionLabel = styled.span`
  flex: 1;
  font-size: var(--font-size-30);
`;

export default MultiSelect;
